import "./serviceslist.css";
import { useEffect, useMemo, useState } from "react";
import API from "src/API2";
import toast from "react-hot-toast";

/* ── Inline icons ───────────────────────────────────────────────── */
const Svg = ({ children, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
const IcSearch = () => <Svg><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></Svg>;
const IcClose  = () => <Svg size={18}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></Svg>;
const IcEdit   = () => <Svg size={14}><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" /></Svg>;
const IcStar   = () => <Svg size={32}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></Svg>;

const fmtMoney = (n) => {
  if (n === null || n === undefined || n === "" || Number(n) === 0) return "—";
  const v = Math.round(Number(n) || 0);
  return String(v).replace(/\B(?=(\d{3})+(?!\d))/g, " ") + " DA";
};

const pricingModeOf = (s) => {
  if (Number(s.price_base) > 0) return { key: "ROOM", label: "Per room" };
  if (Number(s.price_adult) > 0 || Number(s.price_child) > 0) return { key: "PERSON", label: "Per person" };
  return { key: "FREE", label: "Free / included" };
};

const ServicesList = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState("ALL");

  useEffect(() => {
    setLoading(true);
    toast.promise(
      API.get("roomservices").then((resp) => setServices(resp.data.services || [])),
      {
        success: "Services loaded",
        loading: "Fetching services…",
        error: "Could not fetch services",
      },
    ).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return services.filter((s) => {
      const matchesQ =
        !q ||
        (s.name || "").toLowerCase().includes(q) ||
        (s.description || "").toLowerCase().includes(q);
      const mode = pricingModeOf(s).key;
      const matchesFilter = filter === "ALL" || filter === mode;
      return matchesQ && matchesFilter;
    });
  }, [services, searchQuery, filter]);

  const counts = useMemo(() => {
    const acc = { ALL: services.length, ROOM: 0, PERSON: 0, FREE: 0 };
    services.forEach((s) => { acc[pricingModeOf(s).key] += 1; });
    return acc;
  }, [services]);

  const FILTERS = [
    { key: "ALL", label: "All" },
    { key: "ROOM", label: "Per room" },
    { key: "PERSON", label: "Per person" },
    { key: "FREE", label: "Free" },
  ];

  return (
    <div className="sv-list">
      <div className="sv-toolbar">
        <div className="sv-search">
          <IcSearch />
          <input
            type="text"
            placeholder="Search by name or description…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="sv-search__clear" onClick={() => setSearchQuery("")} aria-label="Clear">
              <IcClose />
            </button>
          )}
        </div>
        <div className="sv-filter-pills">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`sv-pill ${filter === f.key ? "is-active" : ""}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
              <span className="sv-pill__count">{counts[f.key] ?? 0}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="sv-card">
        <div className="sv-table-wrap">
          <table className="sv-table">
            <thead>
              <tr>
                <th className="sv-no">#</th>
                <th>Service</th>
                <th>Pricing</th>
                <th>Per room</th>
                <th>Adult</th>
                <th>Child</th>
                <th className="sv-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="sv-loading">Loading services…</td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan="7">
                    <div className="sv-empty">
                      <IcStar />
                      <h3>{services.length ? "No matches" : "No services yet"}</h3>
                      <p>
                        {services.length
                          ? "Nothing matches your filters."
                          : "Create your first service to start bundling it into rates."}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filtered.map((s, i) => {
                  const mode = pricingModeOf(s);
                  return (
                    <tr
                      key={s.id || i}
                      className="sv-row"
                      onClick={() => { window.location.href += "/update/" + s.id; }}
                    >
                      <td className="sv-no">{i + 1}</td>
                      <td>
                        <div className="sv-svc">
                          <span className="sv-svc__icon"><IcStar /></span>
                          <div className="sv-svc__body">
                            <strong>{s.name || "—"}</strong>
                            <span>{s.description?.slice(0, 80) || "No description"}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`sv-mode sv-mode--${mode.key.toLowerCase()}`}>{mode.label}</span>
                      </td>
                      <td className="sv-price">{fmtMoney(s.price_base)}</td>
                      <td className="sv-price">{fmtMoney(s.price_adult)}</td>
                      <td className="sv-price">{fmtMoney(s.price_child)}</td>
                      <td className="sv-actions">
                        <button
                          className="sv-icon-btn"
                          onClick={(e) => { e.stopPropagation(); window.location.href += "/update/" + s.id; }}
                          title="Edit service"
                        >
                          <IcEdit />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {filtered.length > 0 && (
          <p className="sv-table__footnote">
            Showing {filtered.length} of {services.length} · Click a row to edit.
          </p>
        )}
      </div>
    </div>
  );
};

export default ServicesList;
